<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <!-- Header with Add button -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">{{ $t("dmp.outputGroups.title") }}</h5>
          <div class="d-flex gap-1">
            <b-button
              variant="primary"
              size="sm"
              @click="addNewOutputGroup"
              :disabled="isLoading"
              class="text-capitalize"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-25" />
              {{ $t("dmp.outputGroups.addGroup") }}
            </b-button>
          </div>
        </div>

        <div v-if="isLoading" class="text-center py-4">
          <b-spinner
            variant="primary"
            :label="$t('dmp.outputGroups.loadingGroups')"
          ></b-spinner>
          <p class="mt-2 mb-0">{{ $t("dmp.outputGroups.loadingGroups") }}</p>
        </div>

        <b-row v-if="!isLoading">
          <b-col cols="5" class="table-column">
            <!-- Table -->
            <b-table
              :items="outputGroups"
              :fields="tableFields"
              responsive
              striped
              hover
              show-empty
              :empty-text="$t('dmp.outputGroups.noGroupsConfigured')"
              selectable
              select-mode="single"
              @row-selected="onRowSelected"
              sort-by="groupNumber"
              :sort-desc="false"
              class="mb-0"
            >
              <template #cell(groupNumber)="data">
                {{ formatGroupNumber(data.value) }}
              </template>

              <template #cell(groupName)="data">
                {{ data.value }}
              </template>
            </b-table>
          </b-col>

          <b-col cols="7" class="details-column">
            <!-- Selected Group Details Card -->
            <b-card v-if="selectedGroup" no-body>
              <b-card-header class="mb-1 card-header-custom pt-0">
                <h6 class="mb-0">{{ $t("dmp.outputGroups.groupDetails") }}</h6>
              </b-card-header>
              <b-card-body>
                <validation-observer ref="detailFormObserver">
                  <b-form @submit.prevent="saveOutputGroup">
                    <b-row>
                      <b-col cols="12">
                        <validation-provider
                          #default="validationContext"
                          :name="$t('dmp.outputGroups.groupNumber')"
                          :rules="{
                            required: true,
                            numeric: true,
                            min_value: 1,
                            max_value: 20,
                          }"
                        >
                          <b-form-group
                            label="Output Group Number"
                            label-for="groupNumber"
                          >
                            <div class="d-flex align-items-center" v-if="isEditing">
                              <span class="mr-1">{{
                                formatGroupNumber(selectedGroup.groupNumber)
                              }}</span>
                            </div>
                            <b-form-input
                              id="groupNumber"
                              v-model="selectedGroup.groupNumber"
                              type="number"
                              :state="getValidationState(validationContext)"
                              :disabled="isEditing"
                              :class="isEditing ? 'd-none' : ''"
                            />
                          </b-form-group>
                        </validation-provider>
                      </b-col>

                      <b-col cols="12">
                        <validation-provider
                          #default="validationContext"
                          name="Output Group Name"
                          :rules="{ required: true, max: 32 }"
                        >
                          <b-form-group label="Output Group Name" label-for="groupName">
                            <b-form-input
                              id="groupName"
                              v-model="selectedGroup.groupName"
                              type="text"
                              :state="getValidationState(validationContext)"
                              maxlength="32"
                            />
                            <b-form-invalid-feedback>
                              {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </b-col>

                      <!-- Output Numbers 1-8 -->
                      <b-col v-for="index in 8" :key="index" cols="12" md="6">
                        <b-form-group
                          :label="`${$t('dmp.outputGroups.outputNumber')} ${index}`"
                          :label-for="`outputNumber${index}`"
                        >
                          <div class="d-flex gap-2">
                            <b-form-select
                              v-model="outputPrefixes[`outputNumber${index}`]"
                              :options="prefixOptions"
                              style="max-width: 60px"
                              class="mr-1"
                              @change="handlePrefixChange(index)"
                            />
                            <b-form-input
                              :id="`outputNumber${index}`"
                              v-model="outputNumbers[`outputNumber${index}`]"
                              type="text"
                              :maxlength="outputPrefixes[`outputNumber${index}`] ? 2 : 3"
                              @input="validateOutputGroupNumber(index)"
                              @blur="validateOutputGroupNumber(index)"
                            />
                          </div>
                          <b-form-text
                            v-if="outputErrors[`outputNumber${index}`]"
                            class="text-danger"
                          >
                            <small>{{ outputErrors[`outputNumber${index}`] }}</small>
                          </b-form-text>
                        </b-form-group>
                      </b-col>

                      <!-- Action Buttons -->
                      <b-col cols="12" class="d-flex justify-content-end mt-2">
                        <b-button
                          v-if="isEditing"
                          variant="outline-danger"
                          size="sm"
                          class="mr-1"
                          @click="deleteOutputGroup"
                          :disabled="isSaving || isDeleting"
                        >
                          <b-spinner v-if="isDeleting" small class="mr-25" />
                          <feather-icon v-else icon="TrashIcon" size="14" class="mr-25" />
                          <span>{{ $t("actions.delete") }}</span>
                        </b-button>
                        <b-button
                          variant="primary"
                          size="sm"
                          type="submit"
                          :disabled="isSaving || isDeleting"
                          @click="saveOutputGroup"
                        >
                          <b-spinner v-if="isSaving" small class="mr-25" />
                          <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                          <span>{{
                            isSaving ? $t("actions.saving") : $t("actions.save")
                          }}</span>
                        </b-button>
                      </b-col>
                    </b-row>
                  </b-form>
                </validation-observer>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  getCurrentInstance,
} from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Swal from "sweetalert2";
import { useTemplateMode } from "./useTemplateMode";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { formatToThreeDigits, parseThreeDigits } from "./usePanelConfig";

export default {
  name: "ConfigOutputGroups",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    outputGroupsData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const toast = useToast();
    const { getValidationState } = formValidation();
    const { t } = useI18nUtils();
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);

    // State
    const outputGroups = ref([]);
    const isLoading = computed(() => props.loading);
    const isSaving = ref(false);
    const isDeleting = ref(false);
    const selectedGroup = ref(null);
    const isEditing = ref(false);
    const detailFormObserver = ref(null);

    // Prefix/number split state
    const outputPrefixes = ref({});
    const outputNumbers = ref({});
    const outputErrors = ref({});

    // Only D prefix or empty allowed
    const prefixOptions = [
      { value: "", text: "" },
      { value: "D", text: "D" },
      { value: "F", text: "F" },
      { value: "G", text: "G" },
    ];

    // Parse a raw API value like "D01" or "001" into { prefix, number }
    const parseOutputValue = (raw) => {
      if (!raw || raw === "" || raw === "000") return { prefix: "", number: "" };
      const match = String(raw).match(/^([DFG])?(\d+)$/);
      if (match) {
        const prefix = match[1] || "";
        const number = parseInt(match[2], 10);
        return { prefix, number: String(number) };
      }
      return { prefix: "", number: "" };
    };

    // Validate a single output slot and write error
    const validateOutputGroupNumber = (index) => {
      const key = `outputNumber${index}`;
      const prefix = outputPrefixes.value[key] || "";
      const raw = outputNumbers.value[key] || "";
      const clean = raw.replace(/\D/g, "");
      outputNumbers.value[key] = clean;

      if (clean === "" || clean === "0") {
        outputErrors.value[key] = "";
        return;
      }

      const num = parseInt(clean, 10);
      if (prefix === "D") {
        if (num < 1 || num > 96) {
          outputErrors.value[key] = "Range: 1-96";
        } else {
          outputErrors.value[key] = "";
        }
      } else if (prefix === "F" || prefix === "G") {
        if (num < 1 || num > 20) {
          outputErrors.value[key] = "Range: 1-20";
        } else {
          outputErrors.value[key] = "";
        }
      } else {
        if (num < 1 || num > 999) {
          outputErrors.value[key] = "Range: 1-999";
        } else {
          outputErrors.value[key] = "";
        }
      }
    };

    // When prefix changes, clear the number if it is now out of range
    const handlePrefixChange = (index) => {
      const key = `outputNumber${index}`;
      const prefix = outputPrefixes.value[key] || "";
      const raw = outputNumbers.value[key] || "";
      const num = parseInt(raw, 10);
      if (prefix === "D" && !isNaN(num) && num > 96) {
        outputNumbers.value[key] = "";
      }
      outputErrors.value[key] = "";
    };

    // Table fields
    const tableFields = computed(() => [
      { key: "groupNumber", label: "Number", sortable: true },
      { key: "groupName", label: "Name", sortable: true },
    ]);

    // Helper function to format group number (1 -> 001)
    const formatGroupNumber = (value) => value;

    // Helper function to parse group number from API format (001 -> 1)
    const parseGroupNumber = (value) => parseThreeDigits(value);

    // Validate that at least one output is provided
    const validateAtLeastOneOutput = () => {
      return Array.from({ length: 8 }, (_, i) => i + 1).some((i) => {
        const key = `outputNumber${i}`;
        const num = parseInt(outputNumbers.value[key], 10);
        return !isNaN(num) && num >= 1;
      });
    };

    // Build payload for API request
    const buildOutputGroupPayload = (formData) => {
      const buildValue = (index) => {
        const key = `outputNumber${index}`;
        const prefix = outputPrefixes.value[key] || "";
        const raw = outputNumbers.value[key] || "";
        const num = parseInt(raw, 10);
        if (!raw || isNaN(num) || num < 1) return "000";
        if (prefix === "D" || prefix === "F" || prefix === "G") {
          return `${prefix}${String(num).padStart(2, "0")}`;
        }
        return String(num).padStart(3, "0");
      };

      return {
        groupNumber: formatGroupNumber(formData.groupNumber),
        groupName: formData.groupName.toUpperCase(),
        outputNumber1: buildValue(1),
        outputNumber2: buildValue(2),
        outputNumber3: buildValue(3),
        outputNumber4: buildValue(4),
        outputNumber5: buildValue(5),
        outputNumber6: buildValue(6),
        outputNumber7: buildValue(7),
        outputNumber8: buildValue(8),
      };
    };

    // Show toast notification
    const showToast = (title, text, variant, icon) => {
      toast({
        component: ToastificationContent,
        props: {
          title: proxy.$t(title),
          text: proxy.$t(text),
          icon,
          variant,
        },
      });
    };

    // Common save logic for both create and update
    const performSaveOutputGroup = async (formData, isEdit = false) => {
      // Validate at least one output is provided
      if (!validateAtLeastOneOutput()) {
        showToast(
          "dmp.common.error",
          "dmp.outputGroups.messages.atLeastOneOutput",
          "warning",
          "AlertTriangleIcon"
        );
        return false;
      }

      if (isTemplateMode.value) {
        const payload = buildOutputGroupPayload(formData);
        const list = [...(props.outputGroupsData || [])];
        const idx = list.findIndex(
          (g) => parseInt(g.groupNumber) === parseInt(payload.groupNumber)
        );
        const item = { ...(idx !== -1 ? list[idx] : {}), ...payload };
        if (idx !== -1) {
          item.id = list[idx].id != null ? list[idx].id : nextSequentialId(list);
          list.splice(idx, 1, item);
        } else {
          item.id = nextSequentialId(list);
          list.push(item);
        }

        emitTemplateUpdate("output_groups", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t(
              isEdit
                ? "dmp.outputGroups.messages.groupUpdatedSuccess"
                : "dmp.outputGroups.messages.groupAddedSuccess"
            ),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedGroup.value = null;
        isEditing.value = false;
        return true;
      }

      isSaving.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        const payload = buildOutputGroupPayload(formData);
        const response = await store.dispatch("dmp_panels/setOutputGroup", {
          uuid: props.uuid,
          outputGroupData: payload,
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t(
                isEdit
                  ? "dmp.outputGroups.messages.groupUpdatedSuccess"
                  : "dmp.outputGroups.messages.groupAddedSuccess"
              ),
              icon: "CheckIcon",
              variant: "success",
            },
          });

          selectedGroup.value = null;
          isEditing.value = false;
          return true;
        } else {
          throw new Error(
            response.message || `Failed to ${isEdit ? "update" : "create"} output group`
          );
        }
      } catch (error) {
        console.error(`Error ${isEdit ? "updating" : "creating"} output group:`, error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.outputGroups.messages.groupSaveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return false;
      } finally {
        isSaving.value = false;
        store.commit("dmp_panels/setOperationActive", false);
      }
    };

    const loadOutputGroups = () => {
      if (!props.outputGroupsData) {
        outputGroups.value = [];
        return;
      }

      try {
        outputGroups.value = props.outputGroupsData;
      } catch (error) {
        console.error("Error loading output groups:", error);
        outputGroups.value = [];
      }
    };

    // Handle row selection
    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];
        isEditing.value = true;

        selectedGroup.value = {
          groupNumber: parseGroupNumber(selected.groupNumber),
          groupName: selected.groupName,
        };

        // Parse each output slot into prefix + number
        const prefixes = {};
        const numbers = {};
        const errors = {};
        for (let i = 1; i <= 8; i++) {
          const key = `outputNumber${i}`;
          const parsed = parseOutputValue(selected[key]);
          prefixes[key] = parsed.prefix;
          numbers[key] = parsed.number;
          errors[key] = "";
        }
        outputPrefixes.value = prefixes;
        outputNumbers.value = numbers;
        outputErrors.value = errors;
      } else {
        selectedGroup.value = null;
        isEditing.value = false;
      }
    };

    // Add new output group
    const addNewOutputGroup = () => {
      isEditing.value = false;
      selectedGroup.value = {
        groupNumber: "",
        groupName: "",
      };
      const prefixes = {};
      const numbers = {};
      const errors = {};
      for (let i = 1; i <= 8; i++) {
        const key = `outputNumber${i}`;
        prefixes[key] = "";
        numbers[key] = "";
        errors[key] = "";
      }
      outputPrefixes.value = prefixes;
      outputNumbers.value = numbers;
      outputErrors.value = errors;
    };

    // Save output group
    const saveOutputGroup = async () => {
      const isValid = await detailFormObserver.value.validate();
      if (!isValid) return;

      await performSaveOutputGroup(selectedGroup.value, isEditing.value);
    };

    // Delete output group
    const deleteOutputGroup = async () => {
      if (!isEditing.value) {
        return;
      }

      const result = await Swal.fire({
        title: proxy.$t("messages.delete.title"),
        text: proxy.$t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isTemplateMode.value) {
        const list = (props.outputGroupsData || []).filter(
          (g) =>
            parseInt(g.groupNumber) !==
            parseInt(formatGroupNumber(selectedGroup.value.groupNumber))
        );
        emitTemplateUpdate("output_groups", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.outputGroups.messages.groupDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedGroup.value = null;
        isEditing.value = false;
        return;
      }

      isDeleting.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        const response = await store.dispatch("dmp_panels/deleteOutputGroup", {
          uuid: props.uuid,
          groupNumber: formatGroupNumber(selectedGroup.value.groupNumber),
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.outputGroups.messages.groupDeleteSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });

          selectedGroup.value = null;
          isEditing.value = false;
        } else {
          throw new Error(response.message || "Failed to delete output group");
        }
      } catch (error) {
        console.error("Error deleting output group:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.outputGroups.messages.groupDeleteError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isDeleting.value = false;
        store.commit("dmp_panels/setOperationActive", false);
      }
    };

    watch(
      () => props.outputGroupsData,
      (newData) => {
        if (newData) {
          outputGroups.value = [...newData];
        }
      },
      { deep: true }
    );

    onMounted(() => {
      loadOutputGroups();
    });

    return {
      // State
      outputGroups,
      isLoading,
      isSaving,
      isDeleting,
      selectedGroup,
      isEditing,
      detailFormObserver,

      // Prefix/number
      outputPrefixes,
      outputNumbers,
      outputErrors,
      prefixOptions,

      // Table
      tableFields,

      // Methods
      onRowSelected,
      addNewOutputGroup,
      saveOutputGroup,
      deleteOutputGroup,
      loadOutputGroups,
      validateAtLeastOneOutput,
      validateOutputGroupNumber,
      handlePrefixChange,
      formatGroupNumber,
      buildOutputGroupPayload,
      performSaveOutputGroup,
      showToast,
      t,

      // Utils
      getValidationState,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }
}
</style>
